import React, { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import LogoImage from '@plone/volto/components/theme/Logo/Logo.svg';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import type { Content } from '@plone/types';

import logoDark from './CameraUberlandia.psd.png';
import logoLight from './xbrasao.png';

const messages = defineMessages({
  home: {
    id: 'Home',
    defaultMessage: 'Home',
  },
  logoOf: {
    id: 'Logo of',
    defaultMessage: 'Logo of',
  },
});

type ThemeName = 'light' | 'dark' | 'high-contrast';

type FormState = {
  content: {
    data: Content;
  };
  navroot: {
    data: {
      navroot: Content;
    };
  };
  site: {
    data: Content['@components']['site'];
  };
  form: {
    global: Content;
  };
};

const isThemeName = (value: string | null): value is ThemeName =>
  value === 'light' || value === 'dark' || value === 'high-contrast';

const getInitialTheme = (): ThemeName => {
  if (typeof window === 'undefined') return 'light';

  try {
    const documentTheme = document.documentElement.getAttribute('data-theme');
    if (isThemeName(documentTheme)) return documentTheme;

    const savedTheme = localStorage.getItem('theme');
    if (isThemeName(savedTheme)) return savedTheme;

    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  } catch {
    return 'light';
  }
};

const HeaderLogo = ({ isFooterLogo }: { isFooterLogo?: boolean }) => {
  const intl = useIntl();
  const site = useSelector<FormState, Content>((state) => state.site.data);
  const navroot = useSelector<FormState, Content | undefined>(
    (state) => state.navroot?.data,
  );
  const navRootPath = flattenToAppURL(navroot?.navroot?.['@id']) || '/';
  const [theme, setTheme] = useState<ThemeName>('light');
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const syncTheme = () => setTheme(getInitialTheme());

    const observer = new MutationObserver((mutations) => {
      if (
        mutations.some((mutation) => mutation.attributeName === 'data-theme')
      ) {
        syncTheme();
      }
    });

    syncTheme();
    setMounted(true);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });

    return () => observer.disconnect();
  }, []);

  const logoFallback = site['plone.site_logo']
    ? flattenToAppURL(site['plone.site_logo'])
    : LogoImage;
  const currentTheme = mounted ? theme : 'light';
  const logoSrc = currentTheme === 'light' ? logoLight : logoDark;

  return (
    <Link to={navRootPath} aria-label={intl.formatMessage(messages.home)}>
      <img
        src={logoSrc || logoFallback}
        alt={
          intl.formatMessage(messages.logoOf) + ' ' + site['plone.site_title']
        }
      />
    </Link>
  );
};

export default HeaderLogo;
